import React, { useState } from 'react';

import { useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, ArrowBack, Delete, Edit, OpenInNew } from '@mui/icons-material';
import { ColWidths, TableCellEllipsis, TableCellHead, TableCellIcon } from '../components/TableComponents';
import { formatDateOnly } from '../Formatters';
import { StatusChip, TagsCell } from '../guides/GuideStatusCells';
import { BackButton, StyledLink } from '../Components';
import { APPLICATION_SET_STATUS, APPLICATION_SET_OWNER, APPLICATION_SET_UNDER_REVISION, GET_APP_DETAILS } from './apps.graphql';
import { REMOVE_APP_LINK } from '../guides/guide.graphql';
import { AppStatusIcon, appType, statusText } from './AppTable';
import { useMutation, useQuery } from '@apollo/client';
import LoadingSpinner from '../LoadingSpinner';
import { AppGuideItemFragment, ApplicationStatus, GetAppDetailsQuery } from '../__generated__/graphql';
import { StyledPaper } from '../theme';
import { APP_PAGE_MAX_WIDTH, AppsError } from './Overview';
import { UserSelector } from '../users/UserSelector';
import { AppGuidePicker } from './AppGuidePicker';
import { AppDetailsDialog } from './AppDetailsDialog';

type App = NonNullable<GetAppDetailsQuery['app']>;

interface Row {
  guide: AppGuideItemFragment;
  embedded: boolean;
  linked: boolean;
}

/** An app's own page: what FagAdmin keeps on it, and the guides it is mapped to. */
export function AppDetail() {
  const { appId } = useParams();
  const { data, error, refetch } = useQuery(GET_APP_DETAILS, { variables: { appId: appId! }, fetchPolicy: 'cache-and-network' });
  const app = data?.app;

  if (error) {
    return (
      <StyledPaper>
        <AppsError onRetry={() => refetch()} />
      </StyledPaper>
    );
  }
  if (!data) return <LoadingSpinner />;
  if (!app) {
    return (
      <StyledPaper>
        <Typography>
          <BackButton to="/apps" />
          Fant ikke applikasjonen i Apps
        </Typography>
      </StyledPaper>
    );
  }

  return (
    // Gap rather than spacing: spacing resets the children's margins, and the paper's right margin with them.
    <Stack gap={1} sx={{ maxWidth: APP_PAGE_MAX_WIDTH, mx: 'auto' }}>
      {/* Above the cards rather than in one, so the card's content keeps its full width. Always to the
          overview: coming from a guide's panel, going back one step would land on the guide. */}
      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
        <ArrowBack fontSize="small" color="action" />
        <StyledLink to="/apps">Applikasjoner</StyledLink>
      </Typography>
      <AppDetails app={app} onChanged={() => refetch()} />
      <AppGuides app={app} onChanged={() => refetch()} />
    </Stack>
  );
}

/**
 * Two cards: on the left what comes from the Apps catalog (title, description, category, type), on
 * the right what FagAdmin keeps. The left card is edited in a dialog that writes to Apps, so it is
 * kept apart from the fields edited here.
 */
function AppDetails({ app, onChanged }: { app: App; onChanged(): void }) {
  const [editing, setEditing] = useState(false);
  const [setStatus] = useMutation(APPLICATION_SET_STATUS);
  const [setOwner] = useMutation(APPLICATION_SET_OWNER);
  const [setUnderRevision] = useMutation(APPLICATION_SET_UNDER_REVISION);

  const application = app.application;
  const type = appType(app);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} alignItems="stretch">
      <StyledPaper sx={{ flex: 7 }}>
        <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap">
          <Box sx={{ minWidth: 0, flex: '1 1 0' }}>
            <Typography variant="h5">{app.name}</Typography>
            {app.description && (
              <Typography variant="body1" color="textSecondary" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                Beskrivelse:
                <br />
                {app.description}
              </Typography>
            )}
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
              {app.category && <Chip label={app.category} size="small" variant="outlined" />}
              {type && <Chip label={type} size="small" variant="outlined" />}
            </Stack>
          </Box>
          {/* On a phone the buttons get a row of their own under the chips, so the title keeps the card's width. */}
          <Stack direction="row" gap={1} sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
            {/* The app running in Apps; it opens for a user with access to it. */}
            <Button size="small" startIcon={<OpenInNew />} href={app.url} target="_blank" rel="noopener noreferrer">
              Åpne
            </Button>
            <Button size="small" startIcon={<Edit />} onClick={() => setEditing(true)}>
              Rediger
            </Button>
          </Stack>
        </Stack>
        {editing && (
          <AppDetailsDialog
            app={app}
            onClose={(saved) => {
              setEditing(false);
              if (saved) onChanged();
            }}
          />
        )}
      </StyledPaper>
      <StyledPaper sx={{ flex: 5 }}>
        <Stack gap={2}>
          <Field label="Status">
            {/* Only a status: making the app available to customers is done in Apps. */}
            <Select
              variant="standard"
              value={application.status}
              renderValue={(status) => <StatusValue status={status} />}
              onChange={(e) => setStatus({ variables: { input: { appId: app.id, status: e.target.value as ApplicationStatus } } })}>
              {[ApplicationStatus.New, ApplicationStatus.Active].map((status) => (
                <MenuItem key={status} value={status}>
                  <StatusValue status={status} />
                </MenuItem>
              ))}
            </Select>
          </Field>
          <Field label="Ansvarlig">
            <UserSelector
              userId={application.ownerId ?? undefined}
              onChange={(userId) => setOwner({ variables: { input: { appId: app.id, ownerId: userId ?? null } } })}
            />
          </Field>
          <Field label="Under revisjon">
            <Switch
              checked={application.underRevision}
              onChange={(_, checked) => setUnderRevision({ variables: { input: { appId: app.id, underRevision: checked } } })}
            />
          </Field>
        </Stack>
      </StyledPaper>
    </Stack>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      {children}
    </div>
  );
}

function StatusValue({ status }: { status: ApplicationStatus }) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <AppStatusIcon status={status} fontSize="small" />
      {statusText(status)}
    </Stack>
  );
}

/** The guides the app is in or linked to, laid out like the guides in a book. Only the links can be removed here. */
function AppGuides({ app, onChanged }: { app: App; onChanged(): void }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [remove, { error: removeError }] = useMutation(REMOVE_APP_LINK);

  // A guide can both embed the app and have it linked; it is one row then.
  const rows = new Map<string, Row>();
  for (const guide of app.embeddedIn ?? []) {
    if (guide) rows.set(guide.id, { guide, embedded: true, linked: false });
  }
  for (const guide of app.linkedTo ?? []) {
    if (!guide) continue;
    const row = rows.get(guide.id);
    if (row) row.linked = true;
    else rows.set(guide.id, { guide, embedded: false, linked: true });
  }
  const sorted = [...rows.values()].sort((a, b) => (a.guide.docName ?? '').localeCompare(b.guide.docName ?? ''));

  const source = (row: Row) => [row.embedded && 'Inkludert', row.linked && 'Koblet'].filter(Boolean).join(', ');
  const linkedGuideIds = new Set((app.linkedTo ?? []).filter((x) => !!x).map((x) => x!.id));

  const handleRemove = async (guideId: string) => {
    try {
      await remove({ variables: { input: { id: guideId, appId: app.id } } });
      onChanged();
    } catch {
      // The error is shown above the table from the mutation's state.
    }
  };
  const handleClosePicker = () => {
    setPickerOpen(false);
    onChanged();
  };
  // Only the hand-made link can go; an app in the content is removed in the content.
  const removeButton = (row: Row) =>
    row.linked && (
      <IconButton size="small" aria-label={`Fjern koblingen til ${row.guide.docName}`} onClick={() => handleRemove(row.guide.id)}>
        <Delete fontSize="small" />
      </IconButton>
    );

  return (
    <StyledPaper>
      <Typography variant="h5" component="h2">
        Anvisninger&nbsp;
        {/* Only an app that can stand on its own is linked by hand; one that goes in guides is linked by
            including it in the guide's content. */}
        {app.isStandalone && (
          <IconButton onClick={() => setPickerOpen(true)} size="small" aria-label="Koble til anvisning">
            <Add />
          </IconButton>
        )}
      </Typography>
      {!app.isStandalone && (
        <Typography variant="body2" color="textSecondary">
          Knyttes til en anvisning ved at applikasjonen legges inn i anvisningens innhold.
        </Typography>
      )}
      {removeError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Kunne ikke fjerne koblingen. Prøv igjen.
        </Alert>
      )}
      {rows.size === 0 && <Typography sx={{ mt: 1 }}>Ikke knyttet til noen anvisninger</Typography>}
      {/* The table needs about 600px; on a phone each guide is a list item with the columns folded into
          its second line. */}
      {rows.size > 0 && isMobile && (
        <List dense disablePadding>
          {sorted.map((row) => (
            <ListItem key={row.guide.id} divider disableGutters secondaryAction={removeButton(row)}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <StatusChip guide={row.guide} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    <StyledLink to={`/guide/${row.guide.id}`}>{row.guide.docName}</StyledLink> {row.guide.docTitle}
                  </>
                }
                secondaryTypographyProps={{ component: 'div' }}
                secondary={
                  <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                    {[row.guide.mainCategory?.title, source(row), formatDateOnly(row.guide.lastPublishedAt)].filter(Boolean).join(' · ')}
                    <TagsCell guide={row.guide} />
                  </Stack>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      {rows.size > 0 && !isMobile && (
        <Table size="small">
          <ColWidths widths={[40, 40, null, 150, 90, 40, 140, 40]} />
          <TableHead>
            <TableRow>
              <TableCellHead>Status</TableCellHead>
              <TableCellHead>Nummer</TableCellHead>
              <TableCellHead>Tittel</TableCellHead>
              <TableCellHead>Fagområde</TableCellHead>
              <TableCellHead>Kilde</TableCellHead>
              <TableCellHead>Sist publisert</TableCellHead>
              <TableCellHead>Tagger</TableCellHead>
              <TableCellHead></TableCellHead>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => (
              <TableRow key={row.guide.id}>
                <TableCellIcon>
                  <StatusChip guide={row.guide} />
                </TableCellIcon>
                <TableCell>
                  <StyledLink to={`/guide/${row.guide.id}`}>{row.guide.docName}</StyledLink>
                </TableCell>
                <TableCellEllipsis title={row.guide.docTitle}>{row.guide.docTitle}</TableCellEllipsis>
                <TableCellEllipsis title={row.guide.mainCategory?.title}>{row.guide.mainCategory?.title}</TableCellEllipsis>
                <TableCell>{source(row)}</TableCell>
                <TableCell>{formatDateOnly(row.guide.lastPublishedAt)}</TableCell>
                <TableCell>
                  <TagsCell guide={row.guide} />
                </TableCell>
                <TableCell padding="checkbox">{removeButton(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {pickerOpen && <AppGuidePicker app={app} linkedGuideIds={linkedGuideIds} onClose={handleClosePicker} />}
    </StyledPaper>
  );
}
